import React, { createContext, useContext, useState, useEffect } from 'react';

// Import translation files
import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';
import esTranslations from '../translations/es.json';
import caTranslations from '../translations/ca.json';
import deTranslations from '../translations/de.json';

const translations = {
  en: enTranslations,
  fr: frTranslations,
  es: esTranslations,
  ca: caTranslations,
  de: deTranslations
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translationsData, setTranslationsData] = useState(translations.en);

  // Get language from URL path
  const getLanguageFromURL = () => {
    const path = window.location.pathname;
    const match = path.match(/^\/(en|fr|es|ca|de)/);
    return match ? match[1] : 'en';
  };

  // Update URL when language changes
  const updateURL = (newLanguage) => {
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    
    // Special handling for paths that contain "ca" which might be misinterpreted as language code
    // We need to preserve "campaigns" path correctly
    let processedPath = currentPath;
    
    // If the path contains "campaigns", we need to be careful not to lose "ca"
    if (currentPath.includes('campaigns')) {
      // Temporarily replace "campaigns" with a placeholder
      processedPath = currentPath.replace(/campaigns/g, 'CAMPAIGNS_PLACEHOLDER');
    }
    
    // Replace language in path
    const newPath = processedPath.replace(/^\/(en|fr|es|ca|de)/, `/${newLanguage}`);
    
    // If no language in current path, add it
    let finalPath = newPath === processedPath ? `/${newLanguage}${processedPath}` : newPath;
    
    // Ensure there's a slash between language and path if needed
    // Handle cases where processedPath might or might not start with a slash
    if (finalPath.startsWith(`/${newLanguage}`)) {
      // Check if the next character after /{language} is not a slash
      const languagePrefix = `/${newLanguage}`;
      if (finalPath.length > languagePrefix.length && finalPath[languagePrefix.length] !== '/') {
        // Insert a slash after the language prefix
        finalPath = languagePrefix + '/' + finalPath.substring(languagePrefix.length);
      }
    }
    
    // Restore "campaigns" from placeholder
    if (finalPath.includes('CAMPAIGNS_PLACEHOLDER')) {
      finalPath = finalPath.replace(/CAMPAIGNS_PLACEHOLDER/g, 'campaigns');
    }
    
    // Also handle any corrupted paths that might have lost "ca"
    // Only replace if it's exactly /mpaigns or /campa (not part of a longer word)
    finalPath = finalPath.replace(/\/mpaigns(\/|$)/g, '/campaigns$1');
    finalPath = finalPath.replace(/\/campa(\/|$)/g, '/campaigns$1');
    
    // Update URL without page reload
    window.history.replaceState(null, '', finalPath + currentHash);
  };

  // Initialize language from URL or localStorage
  useEffect(() => {
    const urlLanguage = getLanguageFromURL();
    const savedLanguage = localStorage.getItem('your_ngo-language');
    
    // Priority: URL > localStorage > browser language > default (en)
    let initialLanguage = urlLanguage;
    
    // If URL language is just the default (en) and no saved language exists,
    // allow browser detection to override it
    if (initialLanguage === 'en' && !savedLanguage) {
      initialLanguage = null; // Force browser detection
    } else if (!initialLanguage && savedLanguage) {
      initialLanguage = savedLanguage;
    }
    
    if (!initialLanguage) {
      // Detect browser language - check primary language code
      const browserLang = navigator.language;
      const primaryLang = browserLang.split('-')[0];
      
      // Check if primary language code is supported (e.g., 'de', 'fr')
      if (['en', 'fr', 'es', 'ca', 'de'].includes(primaryLang)) {
        initialLanguage = primaryLang;
      } else {
        initialLanguage = 'en';
      }
    }
    
    setLanguage(initialLanguage);
    setTranslationsData(translations[initialLanguage]);
    localStorage.setItem('your_ngo-language', initialLanguage);
    
    // Ensure URL reflects the current language
    if (!urlLanguage || urlLanguage !== initialLanguage) {
      updateURL(initialLanguage);
    }
  }, []);

  const changeLanguage = (newLanguage) => {
    if (['en', 'fr', 'es', 'ca', 'de'].includes(newLanguage) && newLanguage !== language) {
      setLanguage(newLanguage);
      setTranslationsData(translations[newLanguage]);
      localStorage.setItem('your_ngo-language', newLanguage);
      updateURL(newLanguage);
    }
  };

  // Translation function
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = keys.reduce((obj, k) => obj?.[k], translationsData);
    
    // If translation not found, fallback to English
    if (value === undefined && language !== 'en') {
      value = keys.reduce((obj, k) => obj?.[k], translations.en);
    }
    
    // If still not found, return the key
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    // Replace parameters in the string
    if (typeof value === 'string') {
      return value.replace(/{(\w+)}/g, (match, param) => {
        return params[param] !== undefined ? params[param] : match;
      });
    }
    
    return value;
  };

  // Safe HTML translation function for content with HTML tags
  const tHtml = (key, params = {}) => {
    const text = t(key, params);
    return { __html: text };
  };

  const value = {
    language,
    changeLanguage,
    t,
    tHtml,
    translations: translationsData
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
